import { Divider, Flex, Text } from "components/common"
import { IconText } from "components/custom"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { convertCamelCaseToReadable, localisedValue } from "utils"
import theme from "utils/themeVars"
import styles from "./SalesPlaybook.module.less"
import { getAppLanguage } from "configs/useInitializeApp"

const getLocalizedText = item => {
  if (!item) return ""
  if (typeof item === "string") return item
  return localisedValue({ object: item, key: "text" }) || ""
}

const getCategoryLabel = (key, t) => {
  const categoryTranslationMap = {
    negotiation: "Negotiation",
    salesPitch: "Sales Pitch",
    objectionHandling: "Objection Handling",
    trustBuilding: "Trust Building",
  }

  const translationKey = categoryTranslationMap[key]
  if (translationKey) {
    const translated = t(translationKey)
    if (translated && translated !== translationKey) {
      return translated
    }
  }
  return convertCamelCaseToReadable(key)
}

const CategoryInsights = ({ categoryInsights }) => {
  const { t } = useTranslation()
  const language = getAppLanguage()
  const locale = language?.locale?.toUpperCase()
  const categoryKeys = useMemo(
    () => (categoryInsights && typeof categoryInsights === "object" ? Object.keys(categoryInsights) : []),
    [categoryInsights]
  )

  if (!categoryInsights || typeof categoryInsights !== "object" || categoryKeys.length === 0) {
    return null
  }

  const dosKey = locale === "AR" ? "dosAR" : "dos"
  const dontsKey = locale === "AR" ? "dontsAR" : "donts"

  return (
    <Flex vertical gap="24px" align="flex-start" className="w-100">
      {categoryKeys.map((categoryKey, index) => {
        const category = categoryInsights[categoryKey]
        const dosItems = Array.isArray(category?.[dosKey]) ? category[dosKey] : []
        const dontsItems = Array.isArray(category?.[dontsKey]) ? category[dontsKey] : []

        if (dosItems.length === 0 && dontsItems.length === 0) {
          return null
        }

        return (
          <Flex key={categoryKey} vertical gap="12px" className="w-100">
            <Text strong className="fs16 semiBold mbe-0">
              {getCategoryLabel(categoryKey, t)}
            </Text>
            <Flex className={"w-100"} gap="20px">
              {dosItems.length > 0 && (
                <Flex vertical gap="8px" className={styles.dosSection}>
                  <Text strong className={`${styles.dosSectionTitle} fs14 semiBold mbe-0`}>
                    {t("Do's")}
                  </Text>
                  <Flex vertical gap={"12px"}>
                    {dosItems.map((item, index) => (
                      <IconText
                        key={index}
                        icon="CiCircleCheck"
                        iconProps={{ size: 16, style: { lineHeight: "normal", color: theme["primary-color"] } }}
                        title={getLocalizedText(item)}
                        titleProps={{ className: `${styles.listItemText} base-color` }}
                      />
                    ))}
                  </Flex>
                </Flex>
              )}
              {dontsItems.length > 0 && (
                <Flex vertical gap="8px" className={styles.dontsSection}>
                  <Text strong className={`${styles.dontsSectionTitle} fs14 semiBold mbe-0`}>
                    {t("Don'ts")}
                  </Text>
                  <Flex vertical gap={"12px"}>
                    {dontsItems.map((item, index) => (
                      <IconText
                        key={index}
                        icon="IoIosCloseCircleOutline"
                        iconProps={{ size: 16, style: { lineHeight: "normal", color: theme["error-color"] } }}
                        title={getLocalizedText(item)}
                        titleProps={{ className: `${styles.listItemText} base-color` }}
                      />
                    ))}
                  </Flex>
                </Flex>
              )}
            </Flex>
            {index < categoryKeys?.length - 1 && <Divider className="mbe-8" />}
          </Flex>
        )
      })}
    </Flex>
  )
}

export default CategoryInsights
